import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import {
  ArrowLeft,
  Users,
  DollarSign,
  Gamepad2,
  CalendarDays,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Clock,
  Pause,
  Play,
  Table,
  X,
  ExternalLink,
  Youtube,
  Tv,
  Twitter,
  Instagram,
  Globe,
  FolderOpen,
  Pencil,
} from "lucide-react";

function CampaignInsightsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const fileInputRef = useRef(null);

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [toggling, setToggling] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [influencerJoins, setInfluencerJoins] = useState([]);

  useEffect(() => {
    async function fetchCampaign() {
      try {
        const docRef = doc(db, "campaigns", id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          if (data.publisherId !== currentUser?.uid && userProfile?.role !== "admin") {
            navigate("/dashboard");
            return;
          }
          setCampaign(data);
        } else {
          navigate("/dashboard");
        }
      } catch (err) {
        console.error("Failed to fetch campaign:", err);
        navigate("/dashboard");
      }
      setLoading(false);
    }
    fetchCampaign();

    async function fetchJoins() {
      try {
        const q = query(
          collection(db, "campaignJoins"),
          where("campaignId", "==", id)
        );
        const snap = await getDocs(q);
        setInfluencerJoins(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to fetch joins:", err);
      }
    }
    fetchJoins();
  }, [id, currentUser, navigate]);

  async function handleToggleStatus() {
    setToggling(true);
    try {
      const newStatus = campaign.status === "active" ? "paused" : "active";
      await updateDoc(doc(db, "campaigns", id), { status: newStatus });
      setCampaign((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error("Failed to toggle status:", err);
    }
    setToggling(false);
  }

  function parseCsvLine(line) {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ",") {
          result.push(current.trim());
          current = "";
        } else {
          current += ch;
        }
      }
    }
    result.push(current.trim());
    return result;
  }

  function findColumnIndex(headers, keywords) {
    return headers.findIndex((h) =>
      keywords.some((k) => h.includes(k))
    );
  }

  async function handleCsvSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) {
      setUploadError("Please select a CSV file.");
      return;
    }
    setUploadError("");
    setUploadSuccess(false);
    setCsvFile(file);
    setCsvPreview(null);

    try {
      const text = await file.text();
      const lines = text.trim().split("\n").filter((l) => l.trim());
      if (lines.length < 2) {
        setUploadError("CSV must have a header row and at least one data row.");
        setCsvFile(null);
        return;
      }

      // Steam CSVs have a metadata line first (e.g. "utm_3629080_all_..."),
      // then the actual headers on the next line. Detect and skip it.
      let headerLineIdx = 0;
      const firstLineCols = parseCsvLine(lines[0]).map((h) => h.toLowerCase());
      if (firstLineCols.length < 4 || !firstLineCols.some((h) => h.includes("wishlist"))) {
        headerLineIdx = 1;
        if (lines.length < 3) {
          setUploadError("CSV must have a header row and at least one data row.");
          setCsvFile(null);
          return;
        }
      }

      const headers = parseCsvLine(lines[headerLineIdx]).map((h) => h.toLowerCase());

      const utmContentIdx = findColumnIndex(headers, ["content"]);
      const campaignColIdx = findColumnIndex(headers, ["campaign"]);
      const wishlistIdx = findColumnIndex(headers, ["wishlist"]);
      const visitsIdx = findColumnIndex(headers, ["visit"]);
      const purchasesIdx = findColumnIndex(headers, ["purchase"]);
      const activationsIdx = findColumnIndex(headers, ["activation"]);
      const countryIdx = findColumnIndex(headers, ["country"]);

      if (wishlistIdx < 0) {
        setUploadError('Could not find a "Wishlists" column in the CSV. Make sure your Steamworks UTM CSV export contains a Wishlists column.');
        setCsvFile(null);
        return;
      }

      // Helper to find tier price for a country
      const tiers = campaign.pricingTiers || [];
      const useTiers = campaign.useCountryPricing && tiers.length > 0;
      function getTierForCountry(country) {
        if (!useTiers) return null;
        const matched = tiers.find((t) => t.countries && t.countries.includes(country));
        if (matched) return matched;
        const defaultTier = tiers.find((t) => !t.countries || t.countries.length === 0);
        return defaultTier || null;
      }

      const perInfluencer = {};
      const perCountry = {};
      let grandTotalWishlists = 0;
      let grandTotalVisits = 0;
      let grandTotalPurchases = 0;
      let grandTotalActivations = 0;
      let grandTotalTierSpend = 0;

      for (let i = headerLineIdx + 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i]);

        // Only process rows where the campaign column matches this campaign's ID
        if (campaignColIdx >= 0) {
          const rowCampaign = (cols[campaignColIdx] || "").trim();
          if (rowCampaign !== id) continue;
        }

        const wishlists = parseInt(cols[wishlistIdx]) || 0;
        const visits = visitsIdx >= 0 ? (parseInt(cols[visitsIdx]) || 0) : 0;
        const purchases = purchasesIdx >= 0 ? (parseInt(cols[purchasesIdx]) || 0) : 0;
        const activations = activationsIdx >= 0 ? (parseInt(cols[activationsIdx]) || 0) : 0;
        const utmContent = utmContentIdx >= 0 ? (cols[utmContentIdx] || "unknown") : "unknown";
        const country = countryIdx >= 0 ? (cols[countryIdx] || "Unknown") : "Unknown";

        grandTotalWishlists += wishlists;
        grandTotalVisits += visits;
        grandTotalPurchases += purchases;
        grandTotalActivations += activations;

        // Per-country aggregation
        if (!perCountry[country]) {
          perCountry[country] = { wishlists: 0, visits: 0, purchases: 0, activations: 0 };
        }
        perCountry[country].wishlists += wishlists;
        perCountry[country].visits += visits;
        perCountry[country].purchases += purchases;
        perCountry[country].activations += activations;

        // Tier-based spend per row
        if (useTiers) {
          const tier = getTierForCountry(country);
          const tierPrice = tier ? tier.price : 0;
          const actionCount = campaign.campaignType === "purchase" ? purchases : wishlists;
          grandTotalTierSpend += actionCount * tierPrice;
        }

        // Per-influencer aggregation (also track per-country for tier spend)
        if (!perInfluencer[utmContent]) {
          perInfluencer[utmContent] = { wishlists: 0, visits: 0, purchases: 0, activations: 0, countryData: {} };
        }
        perInfluencer[utmContent].wishlists += wishlists;
        perInfluencer[utmContent].visits += visits;
        perInfluencer[utmContent].purchases += purchases;
        perInfluencer[utmContent].activations += activations;
        if (!perInfluencer[utmContent].countryData[country]) {
          perInfluencer[utmContent].countryData[country] = { wishlists: 0, purchases: 0 };
        }
        perInfluencer[utmContent].countryData[country].wishlists += wishlists;
        perInfluencer[utmContent].countryData[country].purchases += purchases;
      }

      // Calculate per-influencer tier spend
      const influencerBreakdown = Object.entries(perInfluencer)
        .map(([utmId, stats]) => {
          let tierSpend = 0;
          if (useTiers) {
            for (const [c, cd] of Object.entries(stats.countryData)) {
              const tier = getTierForCountry(c);
              const tierPrice = tier ? tier.price : 0;
              const actionCount = campaign.campaignType === "purchase" ? cd.purchases : cd.wishlists;
              tierSpend += actionCount * tierPrice;
            }
          }
          return { utmId, wishlists: stats.wishlists, visits: stats.visits, purchases: stats.purchases, activations: stats.activations, tierSpend };
        })
        .sort((a, b) => b.wishlists - a.wishlists);

      // Country breakdown sorted by wishlists desc
      const countryBreakdown = Object.entries(perCountry)
        .map(([country, stats]) => {
          const tier = getTierForCountry(country);
          return { country, ...stats, tierName: tier?.name || "—", tierPrice: tier?.price || 0 };
        })
        .sort((a, b) => b.wishlists - a.wishlists);

      setCsvPreview({
        totalRows: lines.length - headerLineIdx - 1,
        detectedColumns: {
          utmContent: utmContentIdx >= 0,
          wishlists: true,
          visits: visitsIdx >= 0,
          purchases: purchasesIdx >= 0,
          activations: activationsIdx >= 0,
          country: countryIdx >= 0,
        },
        grandTotalWishlists,
        grandTotalVisits,
        grandTotalPurchases,
        grandTotalActivations,
        grandTotalTierSpend,
        influencerBreakdown,
        countryBreakdown,
        headers,
      });
    } catch (err) {
      console.error("CSV parse error:", err);
      setUploadError("Failed to parse CSV file. Please check the format.");
      setCsvFile(null);
    }
  }

  function handleCancelCsv() {
    setCsvFile(null);
    setCsvPreview(null);
    setUploadError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleCsvUpload() {
    if (!csvFile || !csvPreview) return;
    setUploading(true);
    setUploadError("");
    setUploadSuccess(false);

    try {
      const totalWishlists = csvPreview.grandTotalWishlists;
      const totalPurchases = csvPreview.grandTotalPurchases;
      const useTiers = campaign.useCountryPricing && (campaign.pricingTiers || []).length > 0;
      let totalSpent;
      if (useTiers) {
        totalSpent = csvPreview.grandTotalTierSpend;
      } else {
        const pricePerUnit = campaign.campaignType === "purchase" ? (campaign.pricePerPurchase || 0) : (campaign.pricePerWishlist || 0);
        totalSpent = (campaign.campaignType === "purchase" ? totalPurchases : totalWishlists) * pricePerUnit;
      }

      const batch = writeBatch(db);

      batch.update(doc(db, "campaigns", id), {
        totalWishlists,
        totalPurchases,
        totalSpent,
        lastUpdatedAt: serverTimestamp(),
      });

      if (csvPreview.detectedColumns.utmContent) {
        const joinsSnap = await getDocs(
          query(
            collection(db, "campaignJoins"),
            where("campaignId", "==", id)
          )
        );
        const joinsByUtmId = {};
        joinsSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.utmId) joinsByUtmId[data.utmId] = d.id;
        });

        for (const row of csvPreview.influencerBreakdown) {
          const joinDocId = joinsByUtmId[row.utmId];
          if (joinDocId) {
            const joinRef = doc(db, "campaignJoins", joinDocId);
            const updateData = {
              wishlists: row.wishlists,
              visits: row.visits,
              purchases: row.purchases,
              activations: row.activations,
              lastUpdatedAt: serverTimestamp(),
            };
            if (useTiers) {
              updateData.tierSpend = row.tierSpend;
            }
            batch.update(joinRef, updateData);
          }
        }
      }

      await batch.commit();

      setCampaign((prev) => ({
        ...prev,
        totalWishlists,
        totalPurchases,
        totalSpent,
        lastUpdatedAt: { seconds: Math.floor(Date.now() / 1000) },
      }));
      setCsvFile(null);
      setCsvPreview(null);
      setUploadSuccess(true);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setUploadSuccess(false), 5000);
    } catch (err) {
      console.error("CSV upload error:", err);
      setUploadError("Failed to process CSV. Please try again.");
    }
    setUploading(false);
  }

  function formatTimestamp(ts) {
    if (!ts) return "Never";
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return date.toLocaleString();
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!campaign) return null;

  const budgetUsedPercent = campaign.maxBudget
    ? Math.min(100, ((campaign.totalSpent || 0) / campaign.maxBudget) * 100)
    : 0;

  const influencerPercent =
    campaign.maxInfluencers
      ? Math.min(
          100,
          ((campaign.joinedInfluencers || 0) / campaign.maxInfluencers) * 100
        )
      : 0;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-6 mb-10">
          {campaign.coverImageUrl && (
            <div className="sm:w-56 h-36 sm:h-auto rounded-2xl overflow-hidden flex-shrink-0">
              <img
                src={campaign.coverImageUrl}
                alt={campaign.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h1 className="text-2xl font-bold mb-1">{campaign.title}</h1>
                <p className="text-sm text-gray-500">
                  Steam App ID: {campaign.steamAppId}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    campaign.status === "active"
                      ? "bg-green-500/10 text-green-400 border border-green-500/20"
                      : campaign.status === "pending"
                      ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
                      : campaign.status === "rejected"
                      ? "bg-red-500/10 text-red-400 border border-red-500/20"
                      : "bg-gray-500/10 text-gray-400 border border-gray-500/20"
                  }`}
                >
                  {campaign.status}
                </span>
                {(campaign.status === "active" || campaign.status === "paused") && (
                  <button
                    onClick={handleToggleStatus}
                    disabled={toggling}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition-all ${
                      campaign.status === "active"
                        ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/20"
                        : "bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20"
                    } disabled:opacity-50`}
                  >
                    {campaign.status === "active" ? (
                      <><Pause className="w-3 h-3" /> Pause</>
                    ) : (
                      <><Play className="w-3 h-3" /> Resume</>
                    )}
                  </button>
                )}
                <button
                  onClick={() => navigate(`/campaigns/${id}/edit`)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700 transition-all"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-3">{campaign.description}</p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4" />
                {campaign.startDate} — {campaign.endDate}
              </span>
              {campaign.useCountryPricing && campaign.pricingTiers && campaign.pricingTiers.length > 0 ? (
                <span className="flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4" />
                  {campaign.pricingTiers.map((t, i) => (
                    <span key={i} className="text-xs bg-gray-800 px-2 py-0.5 rounded-full">
                      {t.name}: ${t.price}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4" />
                  ${campaign.campaignType === "purchase" ? campaign.pricePerPurchase : campaign.pricePerWishlist}/{campaign.campaignType === "purchase" ? "purchase" : "wishlist"}
                </span>
              )}
            </div>
            {campaign.platforms && campaign.platforms.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {campaign.platforms.map((p) => (
                  <span
                    key={p}
                    className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400 capitalize"
                  >
                    {p === "twitter" ? "X / Twitter" : p}
                  </span>
                ))}
              </div>
            )}
            {campaign.mediaKitUrl && (
              <a
                href={campaign.mediaKitUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 text-sm text-brand-400 hover:text-brand-300 transition-colors"
              >
                <FolderOpen className="w-4 h-4" />
                Media Kit
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {/* Wishlists */}
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-brand-500/10 flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-brand-400" />
              </div>
              <p className="text-sm text-gray-400">{campaign.campaignType === "purchase" ? "Total Purchases" : "Total Wishlists"}</p>
            </div>
            <p className="text-3xl font-bold">
              {campaign.campaignType === "purchase" ? (campaign.totalPurchases || 0).toLocaleString() : (campaign.totalWishlists || 0).toLocaleString()}
            </p>
          </div>

          {/* Joined Influencers */}
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-sm text-gray-400">Joined Influencers</p>
            </div>
            <p className="text-3xl font-bold">
              {campaign.joinedInfluencers || 0}
              <span className="text-base font-normal text-gray-500 ml-1">
                / {campaign.maxInfluencers || "∞"}
              </span>
            </p>
            {campaign.maxInfluencers && (
              <div className="mt-3 w-full bg-gray-800 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full transition-all"
                  style={{ width: `${influencerPercent}%` }}
                />
              </div>
            )}
          </div>

          {/* Money Spent */}
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-sm text-gray-400">Total Spent</p>
            </div>
            <p className="text-3xl font-bold">
              ${(campaign.totalSpent || 0).toLocaleString()}
              <span className="text-base font-normal text-gray-500 ml-1">
                / ${campaign.maxBudget?.toLocaleString()}
              </span>
            </p>
            {campaign.maxBudget && (
              <div className="mt-3 w-full bg-gray-800 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${budgetUsedPercent}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Influencer Performance */}
        {influencerJoins.length > 0 && (
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold">Influencer Performance</h3>
                <p className="text-xs text-gray-500">
                  Performance metrics for each joined influencer.
                </p>
              </div>
            </div>
            <div className="border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-800/50 text-gray-400 text-xs">
                    <th className="text-left py-2.5 px-4 font-medium">Influencer</th>
                    <th className="text-right py-2.5 px-4 font-medium">{campaign.campaignType === "purchase" ? "Purchases" : "Wishlists"}</th>
                    <th className="text-right py-2.5 px-4 font-medium">Visits</th>
                    {campaign.campaignType !== "purchase" && (
                      <th className="text-right py-2.5 px-4 font-medium">Purchases</th>
                    )}
                    <th className="text-right py-2.5 px-4 font-medium">Earnings</th>
                    <th className="text-right py-2.5 px-4 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {[...influencerJoins]
                    .sort((a, b) => (b.wishlists || 0) - (a.wishlists || 0))
                    .map((join, i) => {
                      const useTiers = campaign.useCountryPricing && (campaign.pricingTiers || []).length > 0;
                      const earnings = useTiers
                        ? (join.tierSpend || 0)
                        : campaign.campaignType === "purchase"
                          ? (join.purchases || 0) * (campaign.pricePerPurchase || 0)
                          : (join.wishlists || 0) * (campaign.pricePerWishlist || 0);
                      return (
                        <tr
                          key={join.id}
                          className={`border-t border-gray-800/50 ${i % 2 === 0 ? "" : "bg-gray-900/30"}`}
                        >
                          <td className="py-2.5 px-4">
                            <div>
                              <p className="text-sm font-medium text-gray-200">{join.influencerName || "Unknown"}</p>
                              <code className="text-xs text-gray-500">{join.utmId}</code>
                            </div>
                          </td>
                          <td className="text-right py-2.5 px-4 font-medium">
                            {campaign.campaignType === "purchase"
                              ? (join.purchases || 0).toLocaleString()
                              : (join.wishlists || 0).toLocaleString()}
                          </td>
                          <td className="text-right py-2.5 px-4 text-gray-400">
                            {(join.visits || 0).toLocaleString()}
                          </td>
                          {campaign.campaignType !== "purchase" && (
                            <td className="text-right py-2.5 px-4 text-gray-400">
                              {(join.purchases || 0).toLocaleString()}
                            </td>
                          )}
                          <td className="text-right py-2.5 px-4 text-green-400">
                            ${earnings.toLocaleString()}
                          </td>
                          <td className="text-right py-2.5 px-4 text-gray-500 text-xs">
                            {join.joinedAt?.toDate
                              ? join.joinedAt.toDate().toLocaleDateString()
                              : join.joinedAt?.seconds
                              ? new Date(join.joinedAt.seconds * 1000).toLocaleDateString()
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Influencer Submitted Content */}
        {(() => {
          const joinsWithPosts = influencerJoins.filter(
            (j) => j.submittedPosts && j.submittedPosts.length > 0
          );
          if (joinsWithPosts.length === 0) return null;

          function getPlatformIcon(platform) {
            const icons = { youtube: Youtube, twitch: Tv, twitter: Twitter, instagram: Instagram, tiktok: Globe, other: Globe };
            const Icon = icons[platform] || Globe;
            return <Icon className="w-4 h-4" />;
          }

          return (
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <ExternalLink className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold">Influencer Published Content</h3>
                  <p className="text-xs text-gray-500">
                    Links submitted by influencers for their published posts.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                {joinsWithPosts.map((join) => (
                  <div key={join.id}>
                    <p className="text-sm font-medium text-gray-300 mb-2">
                      {join.influencerName || join.utmId || "Influencer"}
                    </p>
                    <div className="space-y-2">
                      {join.submittedPosts.map((post, i) => (
                        <a
                          key={i}
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3 hover:border-gray-600/50 transition-all group"
                        >
                          <div className="w-7 h-7 rounded-lg bg-gray-700 flex items-center justify-center text-gray-400 flex-shrink-0">
                            {getPlatformIcon(post.platform)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate group-hover:text-brand-400 transition-colors">
                              {post.title || post.url}
                            </p>
                            <p className="text-xs text-gray-600 truncate">{post.url}</p>
                          </div>
                          <span className="text-xs text-gray-600 flex-shrink-0">
                            {new Date(post.addedAt).toLocaleDateString()}
                          </span>
                          <ExternalLink className="w-3.5 h-3.5 text-gray-600 group-hover:text-white transition-colors flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* CSV Upload */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h3 className="font-semibold">Upload Steamworks UTM CSV</h3>
              <p className="text-xs text-gray-500">
                Upload the UTM CSV export from Steamworks. Requires columns: <code className="text-gray-400">Content</code>, <code className="text-gray-400">Wishlists</code>, <code className="text-gray-400">Visits (GMT)</code>.
                Make sure to export the CSV for dates between <span className="text-gray-300 font-medium">{campaign.startDate}</span> and <span className="text-gray-300 font-medium">{campaign.endDate}</span>.
              </p>
            </div>
          </div>

          {/* File Picker */}
          {!csvPreview && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex-1 w-full">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center gap-3 w-full border-2 border-dashed border-gray-700 hover:border-gray-600 rounded-xl py-4 px-4 cursor-pointer transition-colors"
                >
                  <Upload className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-400">
                    Click to select a CSV file
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCsvSelect}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {/* CSV Preview */}
          {csvPreview && (
            <div className="space-y-4">
              {/* Detected Columns */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(csvPreview.detectedColumns).map(([col, found]) => (
                  <span
                    key={col}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      found
                        ? "bg-green-500/10 text-green-400 border border-green-500/20"
                        : "bg-gray-800 text-gray-500 border border-gray-700"
                    }`}
                  >
                    {col === "utmContent" ? "Content" : col}
                    {found ? " ✓" : " ✗"}
                  </span>
                ))}
                <span className="text-xs px-2.5 py-1 rounded-full bg-gray-800 text-gray-400 border border-gray-700">
                  {csvPreview.totalRows} rows
                </span>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-brand-400">
                    {csvPreview.grandTotalWishlists.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">Wishlists</p>
                </div>
                {csvPreview.detectedColumns.visits && (
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-blue-400">
                      {csvPreview.grandTotalVisits.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Visits</p>
                  </div>
                )}
                {csvPreview.detectedColumns.purchases && (
                  <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-purple-400">
                      {csvPreview.grandTotalPurchases.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Purchases</p>
                  </div>
                )}
                <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-green-400">
                    ${(campaign.useCountryPricing && (campaign.pricingTiers || []).length > 0
                      ? csvPreview.grandTotalTierSpend
                      : (campaign.campaignType === "purchase" ? csvPreview.grandTotalPurchases : csvPreview.grandTotalWishlists) * (campaign.campaignType === "purchase" ? (campaign.pricePerPurchase || 0) : (campaign.pricePerWishlist || 0))
                    ).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">Total Spend{campaign.useCountryPricing ? " (tiered)" : ""}</p>
                </div>
              </div>

              {/* Country Breakdown (when tier pricing + country column detected) */}
              {campaign.useCountryPricing && csvPreview.detectedColumns.country && csvPreview.countryBreakdown && csvPreview.countryBreakdown.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-medium text-gray-300">Country Breakdown</h4>
                  </div>
                  <div className="border border-gray-800 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0">
                        <tr className="bg-gray-800/50 text-gray-400 text-xs">
                          <th className="text-left py-2.5 px-4 font-medium">Country</th>
                          <th className="text-left py-2.5 px-4 font-medium">Tier</th>
                          <th className="text-right py-2.5 px-4 font-medium">$/unit</th>
                          <th className="text-right py-2.5 px-4 font-medium">{campaign.campaignType === "purchase" ? "Purchases" : "Wishlists"}</th>
                          <th className="text-right py-2.5 px-4 font-medium">Spend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.countryBreakdown.map((row, i) => {
                          const actionCount = campaign.campaignType === "purchase" ? row.purchases : row.wishlists;
                          return (
                            <tr key={row.country} className={`border-t border-gray-800/50 ${i % 2 === 0 ? "" : "bg-gray-900/30"}`}>
                              <td className="py-2 px-4 text-gray-300">{row.country}</td>
                              <td className="py-2 px-4">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">{row.tierName}</span>
                              </td>
                              <td className="text-right py-2 px-4 text-gray-400">${row.tierPrice.toFixed(2)}</td>
                              <td className="text-right py-2 px-4 font-medium">{actionCount.toLocaleString()}</td>
                              <td className="text-right py-2 px-4 text-green-400">${(actionCount * row.tierPrice).toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Per-Influencer Breakdown */}
              {csvPreview.detectedColumns.utmContent && csvPreview.influencerBreakdown.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Table className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-medium text-gray-300">
                      Per-Influencer Breakdown
                    </h4>
                  </div>
                  <div className="border border-gray-800 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-800/50 text-gray-400 text-xs">
                          <th className="text-left py-2.5 px-4 font-medium">Content (Influencer)</th>
                          <th className="text-right py-2.5 px-4 font-medium">Wishlists</th>
                          {csvPreview.detectedColumns.visits && (
                            <th className="text-right py-2.5 px-4 font-medium">Visits</th>
                          )}
                          {csvPreview.detectedColumns.purchases && (
                            <th className="text-right py-2.5 px-4 font-medium">Purchases</th>
                          )}
                          {csvPreview.detectedColumns.activations && (
                            <th className="text-right py-2.5 px-4 font-medium">Activations</th>
                          )}
                          <th className="text-right py-2.5 px-4 font-medium">Earnings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.influencerBreakdown.map((row, i) => (
                          <tr
                            key={row.utmId}
                            className={`border-t border-gray-800/50 ${
                              i % 2 === 0 ? "" : "bg-gray-900/30"
                            }`}
                          >
                            <td className="py-2.5 px-4">
                              <code className="text-xs text-brand-400">{row.utmId}</code>
                            </td>
                            <td className="text-right py-2.5 px-4 font-medium">
                              {row.wishlists.toLocaleString()}
                            </td>
                            {csvPreview.detectedColumns.visits && (
                              <td className="text-right py-2.5 px-4 text-gray-400">
                                {row.visits.toLocaleString()}
                              </td>
                            )}
                            {csvPreview.detectedColumns.purchases && (
                              <td className="text-right py-2.5 px-4 text-gray-400">
                                {row.purchases.toLocaleString()}
                              </td>
                            )}
                            {csvPreview.detectedColumns.activations && (
                              <td className="text-right py-2.5 px-4 text-gray-400">
                                {row.activations.toLocaleString()}
                              </td>
                            )}
                            <td className="text-right py-2.5 px-4 text-green-400">
                              ${(campaign.useCountryPricing && (campaign.pricingTiers || []).length > 0
                                ? row.tierSpend
                                : (campaign.campaignType === "purchase" ? row.purchases : row.wishlists) * (campaign.campaignType === "purchase" ? (campaign.pricePerPurchase || 0) : (campaign.pricePerWishlist || 0))
                              ).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!csvPreview.detectedColumns.utmContent && (
                <div className="flex items-center gap-2 text-sm text-yellow-400 bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  No <code className="mx-1">Content</code> column found. Wishlists will be added to campaign totals but won't be attributed to individual influencers.
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handleCsvUpload}
                  disabled={uploading}
                  className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-3 rounded-xl transition-all font-semibold text-sm"
                >
                  {uploading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Confirm & Update Stats
                </button>
                <button
                  onClick={handleCancelCsv}
                  disabled={uploading}
                  className="flex items-center gap-2 text-gray-400 hover:text-white px-4 py-3 rounded-xl transition-colors text-sm"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          )}

          {uploadSuccess && (
            <div className="flex items-center gap-2 mt-3 text-sm text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              CSV processed and stats updated successfully!
            </div>
          )}
          {uploadError && (
            <div className="flex items-center gap-2 mt-3 text-sm text-red-400">
              <AlertCircle className="w-4 h-4" />
              {uploadError}
            </div>
          )}
        </div>

        {/* Last Updated */}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600 py-4">
          <Clock className="w-4 h-4" />
          Last updated: {formatTimestamp(campaign.lastUpdatedAt)}
        </div>
      </div>
    </div>
  );
}

export default CampaignInsightsPage;
